import { forwardRef } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  prefix?: ReactNode;
  suffix?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, required, prefix, suffix, className, id, ...rest },
  ref,
) {
  const inputId = id ?? `input-${Math.random().toString(36).slice(2, 9)}`;
  const hasError = Boolean(error);

  const inputCls = cn(
    'block w-full px-3 py-2.5 text-sm transition rounded-md',
    'bg-white text-zinc-950 placeholder-zinc-400',
    'border border-zinc-300',
    'focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand',
    'disabled:bg-zinc-50 disabled:text-zinc-400 disabled:cursor-not-allowed',
    // dark
    'dark:bg-zinc-900 dark:text-zinc-50 dark:placeholder-zinc-500',
    'dark:border-zinc-700',
    'dark:focus:ring-brand-dm dark:focus:border-brand-dm',
    'dark:disabled:bg-zinc-900 dark:disabled:text-zinc-500',
    // error
    hasError && 'border-rose-500 focus:ring-rose-500 focus:border-rose-500',
    hasError && 'dark:border-rose-500',
    // adornment padding
    prefix ? 'pl-9' : '',
    suffix ? 'pr-9' : '',
    className,
  );

  return (
    <label className="block" htmlFor={inputId}>
      {label && (
        <span className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
          {label}
          {required && <span className="text-rose-600 ml-0.5">*</span>}
        </span>
      )}
      <div className="relative">
        {prefix && (
          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-zinc-500 pointer-events-none">
            {prefix}
          </span>
        )}
        <input ref={ref} id={inputId} className={inputCls} aria-invalid={hasError} {...rest} />
        {suffix && (
          <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-zinc-500">
            {suffix}
          </span>
        )}
      </div>
      {hint && !error && (
        <span className="block text-xs text-zinc-500 dark:text-zinc-400 mt-1.5">{hint}</span>
      )}
      {error && (
        <span className="block text-xs text-rose-600 dark:text-rose-400 mt-1.5">{error}</span>
      )}
    </label>
  );
});
